// Radio (DESIGN §3.6.10): Radix RadioGroup. Kreis 18, Rand 1,5 px `fg-tertiary`; gewählt Ring
// `accent-strong` mit Punkt 8. Variante `card`: ganze Kachel `tile` (≥ 72 hoch) als Option,
// gewählt mit Rand 2 px `accent` (z. B. „Separation package · or · PIP“).
import type { ReactNode, Ref } from 'react';
import { RadioGroup as RadioPrimitive } from 'radix-ui';
import { useFieldProps } from './_lib/fieldContext';
import { cn } from '../basis/kern';

export interface RadioOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  /** Nur `card`: Icon oder Kennzahl rechts. */
  aside?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps {
  ref?: Ref<HTMLDivElement>;
  options: RadioOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  variant?: 'default' | 'card';
  orientation?: 'vertical' | 'horizontal';
  disabled?: boolean;
  required?: boolean;
  name?: string;
  'aria-label'?: string;
  className?: string;
}

export function RadioGroup({
  ref,
  options,
  variant = 'default',
  orientation = 'vertical',
  className,
  ...rest
}: RadioGroupProps) {
  const field = useFieldProps({});
  const card = variant === 'card';
  return (
    <RadioPrimitive.Root
      {...rest}
      ref={ref}
      orientation={orientation}
      aria-describedby={field['aria-describedby']}
      aria-invalid={field['aria-invalid']}
      className={cn(
        'flex gap-3',
        orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
        card && orientation === 'horizontal' && '[&>*]:min-w-56 [&>*]:flex-1',
        className,
      )}
    >
      {options.map((option) => (
        <RadioPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={cn(
            'group/radio relative flex items-start gap-3 text-left disabled:cursor-not-allowed',
            card
              ? cn(
                  'min-h-18 rounded-xl border-2 border-transparent bg-tile p-4',
                  'transition-colors duration-(--bar-dur-fast) ease-standard hover:not-disabled:bg-tile-hover',
                  'data-[state=checked]:border-accent',
                )
              : 'rounded-sm py-1',
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              'mt-0.5 inline-flex size-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-fg-tertiary',
              'group-data-[state=checked]/radio:border-accent-strong group-disabled/radio:opacity-50',
            )}
          >
            <span className="size-2 scale-0 rounded-full bg-accent-strong transition-transform duration-(--bar-dur-fast) ease-standard group-data-[state=checked]/radio:scale-100" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span
              className={cn(
                card ? 'text-label-strong' : 'text-body',
                'text-fg group-disabled/radio:text-fg-disabled',
              )}
            >
              {option.label}
            </span>
            {option.description ? (
              <span className="text-label text-fg-secondary">{option.description}</span>
            ) : null}
          </span>
          {card && option.aside ? (
            <span className="shrink-0 text-fg-secondary">{option.aside}</span>
          ) : null}
        </RadioPrimitive.Item>
      ))}
    </RadioPrimitive.Root>
  );
}
